import { ReactComponent as AlertsIcon } from '@elementary/common-ui/assets//20x20/alert-rules.svg';
import { ReactComponent as CatalogIcon } from '@elementary/common-ui/assets/20x20/catalog.svg';
import { ReactComponent as DashboardIcon } from '@elementary/common-ui/assets/20x20/dashboard.svg';
import { ReactComponent as DataHealthIcon } from '@elementary/common-ui/assets/20x20/data-health.svg';
import { ReactComponent as EnvironmentsIcon } from '@elementary/common-ui/assets/20x20/environments.svg';
import { ReactComponent as IncidentsIcon } from '@elementary/common-ui/assets/20x20/incidents.svg';
import { ReactComponent as LineageIcon } from '@elementary/common-ui/assets/20x20/lineage.svg';
import { ReactComponent as ModelPerformanceIcon } from '@elementary/common-ui/assets/20x20/model-performance.svg';
import { ReactComponent as TestConfigIcon } from '@elementary/common-ui/assets/20x20/test-configuration.svg';
import { ReactComponent as TestPerformance } from '@elementary/common-ui/assets/20x20/test-performance.svg';
import { ReactComponent as TestResultsIcon } from '@elementary/common-ui/assets/20x20/test-results.svg';
import { ReactComponent as PullRequestsIcon } from '@elementary/common-ui/assets/20x20/pull-requests.svg';
import { ReactComponent as MetricsIcon } from '@elementary/common-ui/assets/20x20/metrics.svg';
import { NavbarItems } from '@elementary/common-ui/components';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useFlags from 'shared/hooks/useFlags';
import useUrlWithEnv from 'shared/hooks/useUrlWithEnv';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';

const useNavbarItems = () => {
  const { hasFlag } = useFlags();
  const hasAccountPermission = useAccountPermission();
  const hasEnvPermission = useEnvPermission();
  const getUrlWithEnv = useUrlWithEnv();

  const items: NavbarItems = {
    OVERVIEW: [
      {
        key: 'dashboard',
        path: getUrlWithEnv('/report/dashboard'),
        label: 'Dashboard',
        description: 'Review high-level summary',
        icon: <DashboardIcon />,
        hidden: !(
          hasEnvPermission('VIEW_TESTS') && hasEnvPermission('VIEW_ASSETS')
        ),
      },
      {
        key: 'data-health',
        path: getUrlWithEnv('/data-health'),
        label: 'Data Health',
        description: 'Track data health scores',
        icon: <DataHealthIcon />,
        isNew: false,
        hidden: !(
          hasEnvPermission('VIEW_TESTS') && hasEnvPermission('VIEW_ASSETS')
        ),
      },
      {
        key: 'catalog',
        path: getUrlWithEnv('/catalog'),
        label: 'Catalog',
        description: 'Explore and search datasets',
        icon: <CatalogIcon />,
        hidden: !hasEnvPermission('VIEW_ASSETS'),
      },
    ],
    'RESPONSE & TRIAGE': [
      {
        key: 'incidents',
        path: getUrlWithEnv('/incidents'),
        label: 'Incidents',
        description: 'Triage data issues',
        icon: <IncidentsIcon />,
        hidden: !hasEnvPermission('VIEW_INCIDENTS'),
      },
      {
        key: 'lineage',
        path: getUrlWithEnv('/report/lineage'),
        label: 'Lineage',
        description: 'Analyze root cause & impact',
        icon: <LineageIcon />,
        hidden: !hasEnvPermission('VIEW_ASSETS'),
      },
      {
        key: 'metrics',
        path: getUrlWithEnv('/metrics'),
        label: 'Metrics',
        description: 'Explore data quality metrics',
        icon: <MetricsIcon />,
        isNew: false,
        hidden: !hasEnvPermission('VIEW_METRICS'),
      },
    ],
    DETECTION: [
      {
        key: 'test-results',
        path: getUrlWithEnv('/report/test-results'),
        label: 'Test Results',
        description: 'View test results',
        icon: <TestResultsIcon />,
        hidden: !hasEnvPermission('VIEW_TESTS'),
      },
      {
        key: 'configuration',
        path: getUrlWithEnv('/test-management'),
        label: 'Test Configuration',
        description: 'Manage test configurations',
        icon: <TestConfigIcon />,
        hidden: !(
          hasFlag('test_management') &&
          hasEnvPermission('EDIT_TEST_CONFIGURATIONS')
        ),
      },
    ],
    PERFORMANCE: [
      {
        key: 'model-runs',
        path: getUrlWithEnv('/report/model-runs'),
        label: 'Model Performance',
        description: 'Detect pipeline bottlenecks',
        icon: <ModelPerformanceIcon />,
      },
      {
        key: 'test-runs',
        path: getUrlWithEnv('/report/test-runs'),
        label: 'Test Performance',
        description: 'Find frequently failing tests',
        icon: <TestPerformance />,
        hidden: !hasEnvPermission('VIEW_TESTS'),
      },
    ],
    CONFIGURATION: [
      {
        key: 'environments',
        path: '/settings/environments',
        label: 'Environments',
        description: 'Manage integrations',
        icon: <EnvironmentsIcon />,
        hidden: !hasAccountPermission('EDIT_INTEGRATIONS'),
      },
      {
        key: 'alert-rules',
        path: getUrlWithEnv('/alert-rules'),
        label: 'Alert Rules',
        description: 'Configure alert routing',
        icon: <AlertsIcon />,
        hidden: !hasEnvPermission('VIEW_ALERT_RULES'),
      },
      {
        key: 'pull-requests',
        path: getUrlWithEnv('/pull-requests'),
        label: 'Pull Requests',
        description: 'View pull requests',
        icon: <PullRequestsIcon />,
        hidden: !(
          hasEnvPermission('EDIT_TESTS') || hasEnvPermission('EDIT_ASSETS')
        ),
      },
    ],
  };

  return items;
};

export default useNavbarItems;
